import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GavelIcon from "@mui/icons-material/Gavel";
import PersonIcon from "@mui/icons-material/Person";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FlagIcon from "@mui/icons-material/Flag";
import SummarizeIcon from "@mui/icons-material/Summarize";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { fetchMeeting, downloadMeeting, downloadTranscript, deleteMeeting, generateRecordingSummary, regenerateRecordingSummary, getRecordingSummaryStatus, exportReportPdf } from "api/meet/meetAPI";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import LockIcon from "@mui/icons-material/Lock";
import { useSelector } from "react-redux";
import { selectUserInfo } from "redux/userInfo/userInfoSlice";
import useNotificationProvider from "hooks/notificationBar/useNotificationProvider";
import { REPORT_STEPS, ReportPhase } from "./reportProgress/reportSteps";
import { deriveProgress, ReportStatus } from "./reportProgress/deriveProgress";
import ReportProgressPanel from "./reportProgress/ReportProgressPanel";
import AgendaAttachPopover, { AgendaGeneratePayload } from "./agendaAttach/AgendaAttachPopover";

// Types for structured summary
//
// Several fields below are OPTIONAL on the TypeScript side and REQUIRED in the report's JSON
// schema. That split is deliberate, not an oversight: the schema governs what a new generation
// must emit, this interface governs what the renderer must survive reading, and older reports
// carry none of these fields. Do not "fix" the `?` away.
interface KeyPoint {
  point: string;
  importance: "high" | "medium" | "low";
  // Absent on older reports, which carry `point` with no `detail`.
  detail?: string;
}

interface ActionItem {
  task: string;
  owner: string;
  priority: "high" | "medium" | "low";
  deadline: string | null;
}

interface Decision {
  decision: string;
  context?: string;
}

interface Participant {
  identifier: string;
  inferredName: string | null;
  role: string | null;
}

// One reporter's status update. Default-mode reports only; agenda-mode reports omit it.
interface WorkReport {
  owner: string;
  summary: string;
}

// One question-and-answer exchange. Default-mode reports only. `asker` is empty where the
// transcript does not identify them, and `answer` is empty where the question went unanswered.
interface QuestionAnswered {
  asker: string;
  question: string;
  answer: string;
}

// One agenda item's minutes section. Agenda-mode reports only. The `status` literals are the
// schema's own and stay hyphenated in code - `not-reached`, never `not reached`. `body` may be
// empty for a not-reached item that carries no agenda note.
interface AgendaItem {
  heading: string;
  body: string;
  status: "covered" | "deferred" | "not-reached";
}

interface StructuredSummary {
  meetingType: string;
  overallSentiment: string;
  objective?: string;
  executiveSummary?: string;
  workReported?: WorkReport[];
  keyPoints: KeyPoint[];
  questionsAnswered?: QuestionAnswered[];
  decisions: Decision[];
  actionItems: ActionItem[];
  participants: Participant[];
  followUp: {
    needed: boolean;
    reason?: string;
  };
  conclusion?: string;
  tags: string[];
  // Agenda mode only. Absent on every default-mode report and on every historical report.
  agendaItems?: AgendaItem[];
}

// The AI report runs as a background job in Node; this modal just asks where it is every 10 s.
// `stage` is only ever a non-terminal value on the wire - 'ready' and 'failed' arrive in
// `status`, never in `stage`.
// `stage` is carried as a raw string and matched against REPORT_STEPS by deriveProgress. A
// closed union here would be a second place the backend's stage list has to be maintained.

// Shape of POST /api3.2/meet/recordings/:id/summary - either the cached report (200) or a job
// acknowledgement (202). meetAPI.js is plain JS with no .d.ts, so without these declarations the
// payload arrives as `any` under tsconfig "strict": true.
type SummaryPostResponse = {
  success?: boolean;
  summary?: string;
  status?: "started" | "processing";
  stage?: string;
};

// Shape of GET /api3.2/meet/recordings/:id/summary/status. `error` is `unknown` on purpose:
// the summary job sends a string, but the shared /meet rate limiter sends an object.
type SummaryStatusResponse = {
  status?: "processing" | "ready" | "failed";
  stage?: string;
  error?: unknown;
};

// Ask every 10 s. The server-side Whereby backoff also tops out at 10 s - polling faster than
// the user can see the result change is wasted API calls on both sides.
const SUMMARY_POLL_INTERVAL_MS = 10000;

// Client-side ceiling, measured from the click. It is sized to the backend's single-attempt worst
// case: one full streaming attempt is bounded by ANTHROPIC_STREAM_ATTEMPT_DEADLINE_MS, a
// CANapps-unix constant, and this ceiling is that deadline plus slack for the transcription stage
// and job start. The server's own wedge watchdog is far longer, so this can still trip while an
// honest job is running - which is why the message says the report is still being generated, not
// that it failed. Without a ceiling the modal spins forever against a wedged or unreachable
// backend.
//
// COUPLED, ACROSS TWO REPOSITORIES. ANTHROPIC_STREAM_ATTEMPT_DEADLINE_MS lives in CANapps-unix,
// so no single commit can retune both sides. Treat the coupling as a DEPLOY coupling: neither
// branch promotes to an environment without the other. Read the CANapps-unix constant before
// trusting any figure inferred here.
const SUMMARY_POLL_CEILING_MS = 40 * 60 * 1000;

// A 404 from the status endpoint means Node restarted and lost the in-memory job registry.
// Re-POST once to start over. Uncapped this is an infinite POST -> 404 -> POST loop every 10 s,
// and against an OLD backend each of those POSTs spends ~166 s running the whole synchronous
// pipeline (mostly Whereby polling).
const SUMMARY_MAX_AUTO_RESTARTS = 1;

// How long the completed checklist stays up before the report replaces it. Long enough to read
// the finished list - however many steps it ends up carrying - short enough not to delay the
// thing the user clicked for. The value is deliberate, not a placeholder.
const REPORT_READY_HOLD_MS = 900;

// How often the elapsed counters repaint while a job runs. One second is the resolution the m:ss
// display actually shows - anything faster repaints without changing a digit.
const REPORT_TICK_INTERVAL_MS = 1000;

// How old a mirrored timing record may be before a reopen ignores it. This MUST exceed
// SUMMARY_POLL_CEILING_MS above, strictly: a record left behind by a 'detached' job is by
// construction exactly ceiling-aged, so a threshold equal to the ceiling would discard precisely
// the records the detached phase exists to keep. The real "this job cannot still be alive" bound
// is the server's wedge watchdog, so this tracks that, rounded up.
// COUPLED: the wedge is DERIVED, not chosen. SUMMARY_JOB_WEDGE_MS in CANapps-unix sums the
// per-leg budgets and has moved every time a leg was retuned, so read that constant rather than
// trusting any figure inferred here. The strict `>` against SUMMARY_POLL_CEILING_MS is the part
// of the coupling this file can enforce, and it is why the two constants are retuned together
// rather than separately.
const REPORT_TIMING_MAX_AGE_MS = 160 * 60 * 1000;

// Why an action cluster greys out while a report runs. Shown on both halves of the split button:
// a disabled control that explains nothing leaves the user no way to tell a bug from a lock. The
// lock is held by `runningRecordingId`, which outlives the progress dialog, so this tooltip is
// reachable on a table the dialog's backdrop is no longer covering.
const AI_REPORT_RUNNING_TOOLTIP =
  "A report is already being generated — one report runs at a time";

const AGENDA_CARET_TOOLTIP = "Attach an agenda";

// Coerce an arbitrary error payload into something React can actually render. The /meet rate
// limiter answers 429 with { success:false, error:{ code, message } }, so `data.error` can be an
// OBJECT - handing that to setSummaryError throws "Objects are not valid as a React child".
const coerceSummaryMessage = (value: unknown, fallback: string): string => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  if (value && typeof value === "object" && typeof (value as any).message === "string") {
    return (value as any).message;
  }
  return fallback;
};

// Pull a renderable message off an axios error. The 403 session interceptor
// (api/axiosInstance.ts:11-33) rejects with a BARE Error that has no .response at all, so
// error.message is the only thing that survives that case.
// api/utils/errorMessages.ts's getUserFriendlyMessage is deliberately not used here: it has
// zero call sites anywhere in this repo, and it collapses any error into one of six fixed
// sentences, discarding the server-supplied message this modal exists to surface.
const toSummaryErrorMessage = (error: any, fallback: string): string =>
  coerceSummaryMessage(error?.response?.data?.error ?? error?.message, fallback);

// A 401/403 must stop the poll dead: polling cannot outlive the session. For the
// session-expired 403 the interceptor also dispatches showLoginModal(), and a live 10 s
// interval would re-open that modal every 10 seconds - faster than the user can dismiss it.
const isSummaryAuthFailure = (error: any): boolean => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return true;
  }
  return typeof error?.message === "string" && error.message.includes("Session expired");
};

// Per-step timings are measured by the client, so they are lost when the modal is reopened in a
// fresh tab. Mirroring them into sessionStorage keeps them honest within one tab, which is the
// case that actually happens: close the modal, keep working, reopen it. A fresh tab understates
// elapsed - accepted, and the trigger for moving to server-emitted timestamps.
const REPORT_TIMING_STORAGE_PREFIX = "canmeet.report.";

type PersistedReportTiming = {
  // Display only. NEVER written into pollStartedAtRef - that ref is what the client ceiling
  // (SUMMARY_POLL_CEILING_MS) measures from, and seeding it with a restored timestamp would trip
  // the ceiling on reopen.
  startedAt: number;
  enteredAt: Record<string, number>;
  elapsed: Record<string, number>;
};

// Every accessor swallows. sessionStorage throws outright in some privacy modes, and a
// hand-edited value throws on parse. Timings are cosmetic; losing them must never stop a report.
const readReportTiming = (recordingId: string): PersistedReportTiming | null => {
  try {
    const raw = sessionStorage.getItem(`${REPORT_TIMING_STORAGE_PREFIX}${recordingId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.startedAt !== "number") return null;
    // Discard a record too old to belong to a job that could still be running. Unmount and the
    // close handler both leave records behind on purpose, so without this an hour-old entry
    // restores onto a job one second old and shows a 60:00 counter.
    if (Date.now() - parsed.startedAt > REPORT_TIMING_MAX_AGE_MS) return null;
    return {
      startedAt: parsed.startedAt,
      enteredAt: parsed.enteredAt && typeof parsed.enteredAt === "object" ? parsed.enteredAt : {},
      elapsed: parsed.elapsed && typeof parsed.elapsed === "object" ? parsed.elapsed : {},
    };
  } catch {
    return null;
  }
};

const writeReportTiming = (recordingId: string, value: PersistedReportTiming): void => {
  try {
    sessionStorage.setItem(`${REPORT_TIMING_STORAGE_PREFIX}${recordingId}`, JSON.stringify(value));
  } catch {
    /* cosmetic only */
  }
};

const clearReportTiming = (recordingId: string): void => {
  try {
    sessionStorage.removeItem(`${REPORT_TIMING_STORAGE_PREFIX}${recordingId}`);
  } catch {
    /* cosmetic only */
  }
};

const getSentimentColor = (sentiment: string): string => {
  switch (sentiment) {
    case "productive": return "#4caf50";
    case "action-heavy": return "#ff9800";
    case "challenging": return "#f44336";
    default: return "#2196f3";
  }
};

// Map an action item's priority onto an MUI theme colour token. Tokens, never a hex: this app
// stores dark mode as a persisted user setting, so a hardcoded colour is unreadable in one of the
// two themes. The same idea is implemented independently as `getPriorityColor` in
// `src/containers/pages/CANsurvey/AiSummaryDialog.tsx`; that file is not imported here.
const getPriorityColor = (
  priority: string
): "error" | "warning" | "success" | "default" => {
  switch (priority) {
    case "high":
      return "error";
    case "medium":
      return "warning";
    case "low":
      return "success";
    default:
      return "default";
  }
};

// Coverage badges for agenda sections. ONLY `deferred` and `not-reached` are badged - a `covered`
// section carries no badge, which keeps the rendered minutes faithful to the customer's own
// format. Leaving `covered` unbadged is a settled decision, not an omission. The keys are the
// schema's hyphenated literals; the visible labels are prose. Colours are theme tokens so both
// badges stay legible in light and dark mode.
const AGENDA_STATUS_BADGE: Record<
  string,
  { label: string; color: "warning" | "default" }
> = {
  deferred: { label: "Deferred", color: "warning" },
  "not-reached": { label: "Not reached", color: "default" },
};

// Parse summary content - try JSON first, fall back to plain text
const parseSummary = (content: string): StructuredSummary | null => {
  try {
    const parsed = JSON.parse(content);
    // Validate it has expected structure
    if (parsed.keyPoints || parsed.actionItems || parsed.decisions) {
      return parsed as StructuredSummary;
    }
    return null;
  } catch {
    return null;
  }
};

// Component to render structured summary
//
// Section order here must match the PDF renderer in CANapps-unix; the two are maintained by hand
// and nothing enforces the match from this side.
//
// Sections are collected into an array and joined with dividers afterwards rather than written as
// guarded blocks separated by unconditional dividers. That is what stops a divider being drawn
// against a section that guarded itself away: older reports carry none of the newer keys, so a
// reopened old report would otherwise show stranded headings and stranded dividers.
const StructuredSummaryView: React.FC<{ summary: StructuredSummary }> = ({ summary }) => {
  const theme = useTheme();
  // Below `sm` the dialog is narrower than four table columns can hold, so both tables degrade to
  // stacked rows rather than scrolling sideways. A second scrollbar inside the dialog is not
  // allowed, so there is no `TableContainer` and no component-level max-height anywhere below.
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));

  const sectionHeading = (icon: React.ReactNode, text: string) => (
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      {icon}
      <Typography variant="subtitle1" fontWeight="bold">
        {text}
      </Typography>
    </Box>
  );

  const sections: { id: string; node: React.ReactNode }[] = [];

  // Agenda mode. Drawn in array order and never reordered, merged, split, or omitted: the schema
  // fixes the order the model must emit, and this renderer's only job is to preserve it.
  if (summary.agendaItems && summary.agendaItems.length > 0) {
    sections.push({
      id: "agendaItems",
      node: (
        <>
          {sectionHeading(<ListAltIcon color="primary" />, "Agenda")}
          <Stack spacing={2}>
            {summary.agendaItems.map((item, index) => {
              const badge = AGENDA_STATUS_BADGE[item.status];
              return (
                <Box key={index}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {item.heading}
                    </Typography>
                    {badge && <Chip label={badge.label} size="small" color={badge.color} />}
                  </Box>
                  {/* A not-reached item with no agenda note carries an empty body. That is
                      correct output, not a defect, and it must render without a layout break. */}
                  {item.body && <Typography variant="body2">{item.body}</Typography>}
                </Box>
              );
            })}
          </Stack>
        </>
      ),
    });
  }

  // Objective. Absent on older reports, so the heading is guarded rather than drawn over nothing.
  if (summary.objective) {
    sections.push({
      id: "objective",
      node: (
        <>
          {sectionHeading(<FlagIcon color="primary" />, "Objective")}
          <Typography variant="body2">{summary.objective}</Typography>
        </>
      ),
    });
  }

  if (summary.executiveSummary) {
    sections.push({
      id: "executiveSummary",
      node: (
        <>
          {sectionHeading(<SummarizeIcon color="primary" />, "Executive Summary")}
          <Typography variant="body2">{summary.executiveSummary}</Typography>
        </>
      ),
    });
  }

  if (summary.workReported && summary.workReported.length > 0) {
    sections.push({
      id: "workReported",
      node: (
        <>
          {sectionHeading(<RecordVoiceOverIcon color="primary" />, "What Was Reported")}
          <List dense disablePadding>
            {summary.workReported.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.2 }} alignItems="flex-start">
                <ListItemIcon sx={{ minWidth: 24, mt: 1 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.owner}
                  secondary={item.summary || undefined}
                  primaryTypographyProps={{ fontWeight: "medium" }}
                />
              </ListItem>
            ))}
          </List>
        </>
      ),
    });
  }

  // Key Points renders as a label plus a paragraph. `detail` is optional, so it is guarded too: an
  // older report has `point` and no `detail`.
  if (summary.keyPoints?.length > 0) {
    sections.push({
      id: "keyPoints",
      node: (
        <>
          {sectionHeading(<LightbulbIcon color="primary" />, "Key Points")}
          <List dense disablePadding>
            {summary.keyPoints.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.2 }} alignItems="flex-start">
                {/* The bullet only drops to the first text line when there IS a second line, so an
                    older report with no `detail` keeps its original single-line alignment. */}
                <ListItemIcon sx={{ minWidth: 24, mt: item.detail ? 1 : 0 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.point}
                  secondary={item.detail || undefined}
                  primaryTypographyProps={{ fontWeight: "medium" }}
                />
              </ListItem>
            ))}
          </List>
        </>
      ),
    });
  }

  if (summary.questionsAnswered && summary.questionsAnswered.length > 0) {
    sections.push({
      id: "questionsAnswered",
      node: (
        <>
          {sectionHeading(<QuestionAnswerIcon color="primary" />, "Questions and Clarifications")}
          <List dense disablePadding>
            {summary.questionsAnswered.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.2 }} alignItems="flex-start">
                <ListItemIcon sx={{ minWidth: 24, mt: 1 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.question}
                  // Empty `asker` or `answer` values are dropped rather than drawn blank.
                  secondary={
                    [item.asker ? `Asked by ${item.asker}` : "", item.answer]
                      .filter(Boolean)
                      .join(" • ") || undefined
                  }
                  primaryTypographyProps={{ fontWeight: "medium" }}
                />
              </ListItem>
            ))}
          </List>
        </>
      ),
    });
  }

  if (summary.decisions?.length > 0) {
    sections.push({
      id: "decisions",
      node: (
        <>
          {sectionHeading(<GavelIcon sx={{ color: "#9c27b0" }} />, "Decisions Made")}
          {isNarrow ? (
            <Stack spacing={1.5}>
              {summary.decisions.map((item, index) => (
                <Box key={index}>
                  <Typography variant="body2" fontWeight="medium">
                    {item.decision}
                  </Typography>
                  {item.context && (
                    <Typography variant="body2" color="text.secondary">
                      {item.context}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "40%", fontWeight: "bold" }}>Decision</TableCell>
                  <TableCell sx={{ width: "60%", fontWeight: "bold" }}>Context</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.decisions.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ wordBreak: "break-word" }}>{item.decision}</TableCell>
                    {/* `context` is optional, so the `|| ""` is what stops an absent value drawing
                        the word `undefined` into the cell. */}
                    <TableCell sx={{ wordBreak: "break-word" }}>{item.context || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      ),
    });
  }

  // Action Items, as a four-column table. `keyPoints[].importance` is validated and stored but
  // deliberately left unrendered; that is a settled decision, not an oversight.
  if (summary.actionItems?.length > 0) {
    sections.push({
      id: "actionItems",
      node: (
        <>
          {sectionHeading(<AssignmentIcon color="secondary" />, "Action Items")}
          {isNarrow ? (
            <Stack spacing={1.5}>
              {summary.actionItems.map((item, index) => (
                <Box key={index}>
                  <Typography variant="body2" fontWeight="medium">
                    {item.task}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                    {item.owner && (
                      <Typography variant="caption" color="text.secondary">
                        {item.owner}
                      </Typography>
                    )}
                    {item.priority && (
                      <Chip
                        label={item.priority}
                        size="small"
                        color={getPriorityColor(item.priority)}
                      />
                    )}
                    {item.deadline && (
                      <Typography variant="caption" color="text.secondary">
                        {item.deadline}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "45%", fontWeight: "bold" }}>Task</TableCell>
                  <TableCell sx={{ width: "20%", fontWeight: "bold" }}>Owner</TableCell>
                  <TableCell sx={{ width: "17%", fontWeight: "bold" }}>Priority</TableCell>
                  <TableCell sx={{ width: "18%", fontWeight: "bold" }}>Deadline</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.actionItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ wordBreak: "break-word" }}>{item.task}</TableCell>
                    <TableCell sx={{ wordBreak: "break-word" }}>{item.owner}</TableCell>
                    {/* An absent `priority` draws no chip at all, not a blank one. */}
                    <TableCell>
                      {item.priority && (
                        <Chip
                          label={item.priority}
                          size="small"
                          color={getPriorityColor(item.priority)}
                        />
                      )}
                    </TableCell>
                    {/* `deadline` is null unless the transcript stated a date. */}
                    <TableCell sx={{ wordBreak: "break-word" }}>{item.deadline}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      ),
    });
  }

  if (summary.participants?.length > 0) {
    sections.push({
      id: "participants",
      node: (
        <>
          {sectionHeading(<PersonIcon color="action" />, "Participants")}
          <Box display="flex" gap={1} flexWrap="wrap">
            {summary.participants.map((participant, index) => (
              <Chip
                key={index}
                avatar={<Avatar>{(participant.inferredName || participant.identifier)?.[0]?.toUpperCase()}</Avatar>}
                label={
                  participant.inferredName
                    ? `${participant.inferredName}${participant.role ? ` (${participant.role})` : ""}`
                    : participant.identifier
                }
                variant="outlined"
              />
            ))}
          </Box>
        </>
      ),
    });
  }

  if (summary.followUp?.needed) {
    sections.push({
      id: "followUp",
      node: (
        <Box p={2} bgcolor="warning.light" borderRadius={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <EventRepeatIcon color="warning" />
            <Typography variant="subtitle2" fontWeight="bold">
              Follow-up Needed
            </Typography>
          </Box>
          {summary.followUp.reason && (
            <Typography variant="body2" mt={1}>
              {summary.followUp.reason}
            </Typography>
          )}
        </Box>
      ),
    });
  }

  if (summary.conclusion) {
    sections.push({
      id: "conclusion",
      node: (
        <>
          {sectionHeading(<DoneAllIcon color="primary" />, "Conclusion")}
          <Typography variant="body2">{summary.conclusion}</Typography>
        </>
      ),
    });
  }

  return (
    <Box>
      {/* Header with meeting type and sentiment. This chip row is deliberately outside the
          `sections` array, so it always renders first and never takes a divider. */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Chip
          label={summary.meetingType?.replace("-", " ").toUpperCase() || "MEETING"}
          size="small"
          variant="outlined"
        />
        <Chip
          label={summary.overallSentiment?.replace("-", " ") || "General"}
          size="small"
          sx={{
            backgroundColor: getSentimentColor(summary.overallSentiment),
            color: "white",
          }}
        />
        {summary.tags?.map((tag, index) => (
          <Chip key={index} label={tag} size="small" variant="outlined" />
        ))}
      </Box>

      {sections.map((section, index) => (
        <React.Fragment key={section.id}>
          {index > 0 && <Divider sx={{ my: 2 }} />}
          <Box mb={3}>{section.node}</Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

// Format auto-generated meeting names (e.g., "meeting on march 18 2025")
// Capitalizes each word and inserts comma before the year
const formatAutoGeneratedName = (input: string): string => {
  const words = input.trim().split(/\s+/);
  const capitalizedWords = words.map((word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  const titleCaseString = capitalizedWords.join(" ");
  return titleCaseString.replace(/(\d{1,2}) (\d{4})$/, "$1, $2");
};

// What the agenda panel needs about the row whose caret opened it. Exactly the three values
// summarizeRecording takes, so the panel never reaches into the grid's row objects.
type AgendaRow = {
  recordingId: string;
  meetingName: string;
  meetingDate: string;
};

// The optional fourth argument to summarizeRecording. Defaulting the whole object to `{}` is what
// keeps the grid's plain AI Report click a three-argument call that runs default mode.
//
// The replacement flag below is an INTENT here, not a wire key: it is passed along and finally
// handed to startSummaryJob, and the only code that turns it into a request body field is
// postRecordingSummary in api/meet/meetAPI.js. Exactly one caller in this file ever sets it -
// handleAgendaGenerate, which runs only after the user has confirmed the replacement.
type SummarizeOptions = {
  agendaText?: string;
  instructions?: string;
  regenerate?: boolean;
  // Cosmetic, client-held, and never on the wire: the chip in the progress dialog's title.
  agendaFileName?: string;
};

// Displays user's past meeting recordings in a table
const RecordingHistory: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const userInfo = useSelector(selectUserInfo);
  const { notifySuccess, notifyError } = useNotificationProvider();
  const isAdmin = userInfo?.userInfo?.userRoleGroups?.some(
    (g: { name: string }) => g.name === "SDS_Admin"
  );

  // Summary modal state
  const [summaryModalOpen, setSummaryModalOpen] = useState<boolean>(false);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summaryContent, setSummaryContent] = useState<string>("");
  const [summaryMeetingName, setSummaryMeetingName] = useState<string>("");
  const [summaryMeetingDate, setSummaryMeetingDate] = useState<string>("");
  const [summaryError, setSummaryError] = useState<string>("");
  const [summaryRecordingId, setSummaryRecordingId] = useState<string>("");
  const [pdfExporting, setPdfExporting] = useState<boolean>(false);
  const [transcriptDownloading, setTranscriptDownloading] = useState<boolean>(false);
  // "" means "no job is being displayed" - a cached hit, a plain error, or a closed modal. Any
  // other value puts the step panel on screen.
  const [reportPhase, setReportPhase] = useState<ReportPhase | "">("");
  const [reportIndex, setReportIndex] = useState<number>(-1);
  const [reportStepElapsed, setReportStepElapsed] = useState<Record<string, number>>({});
  const [reportTotalElapsed, setReportTotalElapsed] = useState<number>(0);
  // A mid-job transcript failure reports HERE, not through setSummaryError - that path replaces
  // the whole modal body and would destroy the step list the user is watching.
  const [transcriptActionError, setTranscriptActionError] = useState<string>("");

  // The agenda attach panel. The anchor is the caret element that opened it; the row carries the
  // three values summarizeRecording needs. Both are cleared together on close, and the panel is
  // keyed on the recording id below so a different row's caret gets a fresh, empty panel.
  const [agendaAnchorEl, setAgendaAnchorEl] = useState<HTMLElement | null>(null);
  const [agendaRow, setAgendaRow] = useState<AgendaRow | null>(null);

  // The recording whose background job this client is currently watching, or "" when there is
  // none. ONE value carrying two facts, deliberately: whether a job is in flight, and which row
  // owns it.
  //
  // It is armed by startSummaryPolling and released by stopSummaryPolling - the only two functions
  // that arm and disarm the status interval - so it is true for exactly as long as this client is
  // watching a job, INCLUDING while the progress dialog is closed. That is the whole point: it is
  // separate from `summaryLoading`, whose lifetime is the dialog's, and closing the dialog
  // deliberately clears that one (see handleCloseSummaryModal).
  const [runningRecordingId, setRunningRecordingId] = useState<string>("");

  // What this page session has learned about which recordings already carry a stored report.
  //
  // `fetchMeeting` is a Whereby passthrough and says nothing about reports, so this map is the
  // only source that costs no extra request: it records what the summary endpoint's own answers
  // prove.
  //
  // A recording is absent from the map until an answer proves its state, and an ABSENT key is read
  // as "assume a report exists". That direction is not arbitrary: the value only selects confirm
  // copy, and the copy that must never be dropped from a row that has a report is the warning that
  // generating destroys it.
  const [reportPresence, setReportPresence] = useState<Record<string, boolean>>({});

  // Record one proven fact about a recording. A functional updater because a caller runs inside the
  // poll interval's stale closure, and because two answers about the same recording must not race.
  const noteReportPresence = (recordingId: string, present: boolean) => {
    setReportPresence((current) =>
      current[recordingId] === present ? current : { ...current, [recordingId]: present }
    );
  };

  // The attached agenda's file name, for the chip in the progress dialog's title. Cosmetic and
  // client-held: nothing on the wire returns it and no poll response carries it. Deliberately NOT
  // cleared by handleCloseSummaryModal, so closing and reopening the dialog keeps the chip; every
  // path that opens the dialog goes through summarizeRecording, which sets it from its options.
  const [summaryAgendaFileName, setSummaryAgendaFileName] = useState<string>("");

  // REFS, NOT STATE. setInterval captures the render that created it, and this component has no
  // useCallback/useMemo anywhere - so anything a tick reads out of useState would be frozen at
  // its value from that render. The restart cap would never increment and the ceiling would
  // never trip. Precedent: containers/pages/CANsense/hooks/useDeviceMetrics.ts:197-198.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollInFlightRef = useRef<boolean>(false);
  const pollRestartCountRef = useRef<number>(0);
  const pollStartedAtRef = useRef<number>(0);
  const watchedRecordingIdRef = useRef<string>("");

  // Bumped on every open/close, so a late response from a PREVIOUS cycle of the SAME
  // recording cannot pass the guard or clear the current cycle's in-flight flag.
  const pollGenerationRef = useRef<number>(0);

  // Whether the progress dialog is on screen. A REF, not a read of summaryModalOpen, for the same
  // reason as every ref above: the poll tick runs in a closure captured by setInterval, and the
  // poll outlives the dialog, so the render's value would be frozen at whatever it was when
  // the interval was armed. Written beside every setSummaryModalOpen call.
  //
  // It is read only to decide how much work a tick should do once the dialog is gone - never to
  // decide whether the job is alive.
  const summaryModalOpenRef = useRef<boolean>(false);

  // What the CURRENT job was started with. A ref for the same reason as every ref above: the 404
  // auto-restart fires from inside the poll interval, which captured an older render, and it has
  // to re-POST the SAME agenda. A re-POST carrying no agendaText would silently run default mode
  // and produce a report the user never asked for, with nothing on screen saying so.
  //
  // This ref carries no replacement intent, by design. The restart re-POSTs the agenda and never
  // the replacement flag.
  const summaryAgendaRef = useRef<{ agendaText: string; instructions: string }>({
    agendaText: "",
    instructions: "",
  });

  // The monotonic index (deriveProgress rule R5) and the warn-once flag, both scoped to a poll
  // generation. A fresh click or a retry resets them.
  const progressIndexRef = useRef<number>(-1);
  const unknownStageWarnedRef = useRef<boolean>(false);

  // Display clock. SEPARATE from pollStartedAtRef on purpose: pollStartedAtRef is the ceiling's
  // origin and must keep measuring from the click, while this one may be restored from
  // sessionStorage on a reopen.
  const reportStartedAtRef = useRef<number>(0);
  const stepEnteredAtRef = useRef<Record<string, number>>({});
  const stepElapsedRef = useRef<Record<string, number>>({});

  // Stopped on every terminal phase so the numbers freeze where the job stopped rather than
  // counting past it.
  const progressTickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // The one-shot REPORT_READY_HOLD_MS timer that keeps the all-green checklist on screen for a
  // moment before the report takes its place. A ref for the same reason as every timer above: the
  // callback that arms it and the handlers that must cancel it live in different renders. It has
  // to be cancellable, because if the user closes the modal - or leaves CANmeet entirely - inside
  // that window, a timer left running would call setState on a component that is no longer
  // showing the job, or no longer mounted at all.
  const readyHoldTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    getMeetingHistory();
  }, []);

  // Clear the poll on unmount as well as on modal close. Navigating away from CANmeet unmounts
  // this component without ever calling handleCloseSummaryModal, and a live interval would keep
  // firing requests and calling setState on a component that no longer exists.
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (progressTickRef.current) {
        clearInterval(progressTickRef.current);
        progressTickRef.current = null;
      }
      // Unmount is the one teardown path that does NOT go through resetReportProgress, so the
      // ready-hold timer has to be killed by hand here. Left armed, it fires up to
      // REPORT_READY_HOLD_MS after this component is gone and calls setState on it.
      if (readyHoldTimeoutRef.current) {
        clearTimeout(readyHoldTimeoutRef.current);
        readyHoldTimeoutRef.current = null;
      }
      watchedRecordingIdRef.current = "";
    };
  }, []);

  // Download a specific recording by its ID
  const downloadRecording = async (recordingId: string) => {
    try {
      const response = await downloadMeeting(recordingId);
      if (response.accessLink) {
        window.open(response.accessLink, "_blank");
      }
    } catch (error) {
      console.error("[MeetingHistory] Failed to download recording:", error);
      notifyError("Failed to download recording");
    }
  };

  // Delete a specific recording and refresh the table
  const deleteRecording = async (recordingId: string) => {
    try {
      await deleteMeeting(recordingId); // Delete from Whereby cloud
      await getMeetingHistory();       // Refresh list to reflect deletion
      console.log("[MeetingHistory] Recording deleted");
      notifySuccess("Recording deleted successfully!");
    } catch (error) {
      console.error("[MeetingHistory] Failed to delete recording:", error);
      notifyError("Failed to delete recording");
    }
  };

  // Refs only - safe to call from the stale closure setInterval captured.
  const stopProgressTicker = () => {
    if (progressTickRef.current) {
      clearInterval(progressTickRef.current);
      progressTickRef.current = null;
    }
  };

  const persistReportTiming = () => {
    const recordingId = watchedRecordingIdRef.current;
    if (!recordingId || !reportStartedAtRef.current) return;
    writeReportTiming(recordingId, {
      startedAt: reportStartedAtRef.current,
      enteredAt: stepEnteredAtRef.current,
      elapsed: stepElapsedRef.current,
    });
  };

  // Freeze the step we are leaving, stamp entry into the one we are arriving at. Entry is keyed
  // by step key rather than index so a restored record survives the index restarting at -1 (see
  // summarizeRecording): if this step was already entered in an earlier viewing, that timestamp
  // is kept and the counter carries on rather than restarting.
  const stampStepTiming = (previousIndex: number, nextIndex: number) => {
    const now = Date.now();

    if (previousIndex >= 0 && previousIndex < REPORT_STEPS.length) {
      const previousKey = REPORT_STEPS[previousIndex].key;
      const enteredAt = stepEnteredAtRef.current[previousKey];
      if (typeof enteredAt === "number") {
        stepElapsedRef.current = { ...stepElapsedRef.current, [previousKey]: now - enteredAt };
      }
    }

    if (nextIndex >= 0 && nextIndex < REPORT_STEPS.length) {
      const nextKey = REPORT_STEPS[nextIndex].key;
      if (typeof stepEnteredAtRef.current[nextKey] !== "number") {
        stepEnteredAtRef.current = { ...stepEnteredAtRef.current, [nextKey]: now };
      }
    }

    setReportStepElapsed(stepElapsedRef.current);
    // A terminal transition to 'ready' or 'failed' persists here and applyReportProgress clears it
    // moments later. Harmless - the clear wins - and keeping the persist unconditional is what
    // makes the running case a single code path.
    persistReportTiming();
  };

  const startProgressTicker = () => {
    // Nothing renders these counters while the dialog is closed, and the status poll outlives the
    // dialog - so without this line a background job would repaint two pieces of state once a
    // second, re-rendering the whole recordings grid, for its entire remaining life. Reopening the
    // dialog restarts the ticker on the next poll answer, and the per-step durations are restored
    // from the sessionStorage mirror, so nothing is lost by not counting while nobody is looking.
    if (!summaryModalOpenRef.current) return;
    if (progressTickRef.current) return;
    const tick = () => {
      const now = Date.now();
      if (reportStartedAtRef.current) {
        setReportTotalElapsed(now - reportStartedAtRef.current);
      }
      const index = progressIndexRef.current;
      if (index >= 0 && index < REPORT_STEPS.length) {
        const key = REPORT_STEPS[index].key;
        const enteredAt = stepEnteredAtRef.current[key];
        if (typeof enteredAt === "number") {
          stepElapsedRef.current = { ...stepElapsedRef.current, [key]: now - enteredAt };
          setReportStepElapsed(stepElapsedRef.current);
        }
      }
    };
    tick();
    progressTickRef.current = setInterval(tick, REPORT_TICK_INTERVAL_MS);
  };

  // A new job means a new progress generation. deriveProgress rule R5 forbids the index moving
  // backwards WITHIN a generation, so it must be released whenever the backend acknowledges a
  // FRESH job that did not come from a click. Without this the re-attach branch inherits the
  // finished job's terminal index and a brand-new job renders "Opening the report" for its whole
  // life, while the 404 auto-restart holds at the pre-restart step. Only the refs move: the
  // displayed index is left alone so it holds its last honest value until the next answer lands,
  // rather than blanking the rail for one tick.
  const beginReportGeneration = () => {
    progressIndexRef.current = -1;
    unknownStageWarnedRef.current = false;
  };

  // The single point where a wire answer becomes a step index. Every caller passes what it
  // actually received - no caller invents a stage. `error` is forwarded raw and only read on a
  // 'failed' status, where it is the ONLY signal of which step broke: the status endpoint sends no
  // stage alongside a failure (deriveProgress rule R2).
  const applyReportProgress = (status: ReportStatus, stage: string, error?: unknown) => {
    const result = deriveProgress({
      steps: REPORT_STEPS,
      stage,
      status,
      previousIndex: progressIndexRef.current,
      error,
    });

    // Once per poll generation. A stage we do not know holds the display rather than lying about
    // it, and this is the only trace that it happened.
    if (result.unknownStage && !unknownStageWarnedRef.current) {
      unknownStageWarnedRef.current = true;
      console.warn("[MeetingHistory] Unrecognised report stage, holding the current step:", stage);
    }

    if (result.activeIndex !== progressIndexRef.current) {
      stampStepTiming(progressIndexRef.current, result.activeIndex);
      progressIndexRef.current = result.activeIndex;
      setReportIndex(result.activeIndex);
    }

    setReportPhase(result.phase);

    if (result.phase === "running") {
      startProgressTicker();
    } else {
      // Terminal: freeze the numbers where the job stopped and stop mirroring.
      stopProgressTicker();
      const recordingId = watchedRecordingIdRef.current;
      // 'detached' is the one terminal phase where the job is NOT over - the client ceiling
      // tripped and we stopped watching, but Node keeps working. The mirrored timings are kept so
      // a reopen continues the counters instead of restarting them at 0:00.
      if (recordingId && result.phase !== "detached") clearReportTiming(recordingId);
    }
  };

  // Take the panel off screen and forget the job. Used by the cache hit, every non-job error and
  // the close handler.
  const resetReportProgress = (recordingId?: string) => {
    stopProgressTicker();
    // Cancel a ready-hold that has not fired yet. Its callback calls this function, so it must not
    // be allowed to run a second time against whatever state the modal has moved on to - and this
    // is also what covers handleCloseSummaryModal, which reaches the timer through this call.
    // When the timer itself is what got us here it has already nulled the ref, so this is a no-op.
    if (readyHoldTimeoutRef.current) {
      clearTimeout(readyHoldTimeoutRef.current);
      readyHoldTimeoutRef.current = null;
    }
    progressIndexRef.current = -1;
    unknownStageWarnedRef.current = false;
    reportStartedAtRef.current = 0;
    stepEnteredAtRef.current = {};
    stepElapsedRef.current = {};
    setReportPhase("");
    setReportIndex(-1);
    setReportStepElapsed({});
    setReportTotalElapsed(0);
    setTranscriptActionError("");
    if (recordingId) clearReportTiming(recordingId);
  };

  // Stop the status poll. Safe to call from a stale closure: it touches refs and one setState with
  // a constant, neither of which reads a captured render.
  const stopSummaryPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollInFlightRef.current = false;
    // THE SINGLE RELEASE POINT for the cross-row lock. Every way a job stops being watched already
    // routes through this function - a terminal 'ready' or 'failed', the client ceiling, an auth
    // failure, a lost job registry, a POST that never became a job, and the start of a new cycle -
    // so the flag cannot outlive the poll and strand the grid in a disabled state.
    setRunningRecordingId("");
  };

  // The job reported 'ready'. Re-POST to pick the report off the UNCHANGED cached path - the
  // status endpoint never carries the summary, so there is exactly one render path in this UI
  // and SQL stays the single source of truth.
  const loadFinishedSummary = async (recordingId: string) => {
    const generation = pollGenerationRef.current;
    let reattached = false;
    try {
      const response: SummaryPostResponse = await generateRecordingSummary(recordingId);
      if (watchedRecordingIdRef.current !== recordingId || pollGenerationRef.current !== generation) {
        return;
      }
      if (response.success && response.summary) {
        setSummaryContent(response.summary);
        setSummaryError("");
        // The checklist is fully green at this point (applyReportProgress("ready", …) ran before
        // the poll was torn down). Hold it briefly so the completion is legible, then take the
        // panel down and let the report render.
        if (readyHoldTimeoutRef.current) clearTimeout(readyHoldTimeoutRef.current);
        readyHoldTimeoutRef.current = setTimeout(() => {
          readyHoldTimeoutRef.current = null;
          resetReportProgress(recordingId);
        }, REPORT_READY_HOLD_MS);
      } else if (response.status === "started" || response.status === "processing") {
        // The row vanished under us and the POST started a fresh job — watch it rather
        // than abandoning it.
        reattached = true;
        // We only reach here after a 'ready' poll pushed the index to the terminal row. This 202
        // is a DIFFERENT job, so that index has to be released before its first answer lands.
        beginReportGeneration();
        applyReportProgress(response.status, String(response.stage ?? ""));
        startSummaryPolling();
        return;
      } else {
        // The job finished but the report did not come back. Take the panel down first: an
        // all-green checklist sitting above an error message claims the work succeeded, and the
        // plain error is the only honest thing left to show.
        resetReportProgress(recordingId);
        setSummaryError("The report finished but could not be loaded. Please try again.");
      }
    } catch (error: any) {
      if (watchedRecordingIdRef.current !== recordingId || pollGenerationRef.current !== generation) {
        return;
      }
      console.error("[MeetingHistory] Failed to load the finished summary:", error);
      // Same reason as the branch above - a stalled green checklist must not outlive the failure
      // that stopped the report from loading.
      resetReportProgress(recordingId);
      setSummaryError(
        toSummaryErrorMessage(error, "The report finished but could not be loaded. Please try again.")
      );
    } finally {
      // finally runs on the early returns too, hence the guards. The re-attach branch keeps
      // the spinner: polling has just restarted, so this cycle must NOT clear the state.
      if (
        !reattached &&
        watchedRecordingIdRef.current === recordingId &&
        pollGenerationRef.current === generation
      ) {
        setSummaryLoading(false);
      }
    }
  };

  // One status poll. Reads ONLY refs, stable useState setters and module imports, so the stale
  // closure captured by setInterval behaves identically to a fresh one.
  const pollSummaryStatus = async () => {
    const recordingId = watchedRecordingIdRef.current;
    const generation = pollGenerationRef.current;
    if (!recordingId) {
      stopSummaryPolling();
      return;
    }

    // Client ceiling, measured from the click - a 404 restart does not extend it.
    if (Date.now() - pollStartedAtRef.current >= SUMMARY_POLL_CEILING_MS) {
      // Phase first, then the teardown. 'detached' is not a failure: the job is still running in
      // Node and only we have stopped watching it, so applyReportProgress deliberately KEEPS the
      // mirrored timings for this phase and a reopen continues the counters. The call stays above
      // the teardown so it still runs while watchedRecordingIdRef identifies the job.
      applyReportProgress("detached", "");
      stopSummaryPolling();
      // Retire the cycle, same as the close handler: a response still in flight must not pass
      // the guards and restart a job the user was just told to stop waiting for. A re-click
      // re-arms everything (stopSummaryPolling resets the in-flight flag on the next start).
      watchedRecordingIdRef.current = "";
      pollGenerationRef.current += 1;
      setSummaryLoading(false);
      setSummaryError(
        "This report is taking longer than expected. It is still being generated in the background. Close this window and open the report again in a few minutes."
      );
      return;
    }

    // Skip this tick if the previous request has not come back yet.
    if (pollInFlightRef.current) {
      return;
    }
    pollInFlightRef.current = true;

    try {
      const status: SummaryStatusResponse = await getRecordingSummaryStatus(recordingId);
      if (watchedRecordingIdRef.current !== recordingId || pollGenerationRef.current !== generation) {
        return;
      }

      if (status.status === "processing") {
        applyReportProgress("processing", String(status.stage ?? ""));
        return;
      }

      if (status.status === "ready") {
        applyReportProgress("ready", "");
        stopSummaryPolling();
        // The job wrote a report, so this recording now has one, which selects the confirm copy.
        noteReportPresence(recordingId, true);

        // Nobody is watching. Retire the cycle instead of fetching a report no surface would
        // render: reopening this row re-POSTs and gets it from the cache in one request, which is
        // the recovery the progress dialog's own "close and come back" hint promises. Skipping
        // loadFinishedSummary here also keeps its re-attach branch - the one branch in this file
        // that can start a job without a click - out of a state where a started job would be
        // invisible.
        if (!summaryModalOpenRef.current) {
          watchedRecordingIdRef.current = "";
          pollGenerationRef.current += 1;
          return;
        }

        // NOT awaited, same reason as the 404 restart below: loadFinishedSummary owns its own
        // errors and never rejects, and awaiting it would let this tick's `finally` clear the
        // in-flight flag of the NEW poll cycle its re-attach branch can start.
        loadFinishedSummary(recordingId);
        return;
      }

      if (status.status === "failed") {
        stopSummaryPolling();
        applyReportProgress("failed", "", status.error);
        setSummaryLoading(false);
        setSummaryError(
          coerceSummaryMessage(
            status.error,
            "AI Report is not available at this moment. Please contact support."
          )
        );
        return;
      }

      // Any other shape (unrecognised status, empty body) is a transient blip: fall through
      // and let the next tick ask again. The ceiling above bounds how long that can go on.
    } catch (error: any) {
      if (watchedRecordingIdRef.current !== recordingId || pollGenerationRef.current !== generation) {
        return;
      }

      if (isSummaryAuthFailure(error)) {
        stopSummaryPolling();
        resetReportProgress(recordingId);
        setSummaryLoading(false);
        setSummaryError("Your session has expired. Please log in again and reopen the report.");
        return;
      }

      // 404: Node restarted and lost the in-memory registry. Branch on the STATUS CODE ONLY -
      // an old backend answers this URL from the router catch-all with the plain non-JSON body
      // "Route not found" (served as text/html), so there is no JSON field to read.
      if (error?.response?.status === 404) {
        stopSummaryPolling();
        // Nobody is watching, and the registry that knew about this job is gone. There is nothing
        // left to watch, so releasing the cross-row lock is the honest end of the cycle. The
        // restart below is deliberately NOT taken here: it re-POSTs, and against a recording with
        // no stored report that starts a paid generation with no surface showing it and no one
        // waiting for it. A reopen restarts the whole cycle from a click, which is where a decision
        // to spend belongs.
        if (!summaryModalOpenRef.current) {
          watchedRecordingIdRef.current = "";
          pollGenerationRef.current += 1;
          return;
        }
        if (pollRestartCountRef.current >= SUMMARY_MAX_AUTO_RESTARTS) {
          resetReportProgress(recordingId);
          setSummaryLoading(false);
          setSummaryError("AI Report is not available at this moment. Please contact support.");
          return;
        }
        pollRestartCountRef.current += 1;
        console.warn("[MeetingHistory] Summary job not found, restarting once for:", recordingId);
        // The registry is gone, so the re-POST transcribes from scratch. Release the index the
        // lost job had reached, or the display holds a step this new job has not done yet.
        beginReportGeneration();
        // NOT awaited on purpose: startSummaryJob owns its own errors and never rejects, and
        // awaiting it here would let this tick's `finally` clear the in-flight flag of the NEW
        // poll cycle it starts.
        startSummaryJob(recordingId);
        return;
      }

      // Anything else - a dropped connection, a 502 from the proxy, a 429 from the shared
      // /meet rate limiter - is a transient blip. Log it and let the next tick try again; a
      // single failed tick must not kill a job that is still running. The ceiling above is
      // what terminates a persistently broken backend.
      console.error("[MeetingHistory] Summary status poll failed, will retry:", error);
    } finally {
      // Only clear the flag if it is still OUR cycle's. A close + reopen bumps the
      // generation, and this late response must not clear the NEW cycle's flag.
      if (pollGenerationRef.current === generation) {
        pollInFlightRef.current = false;
      }
    }
  };

  // Begin the 10 s heartbeat. Fires one poll immediately so a fast failure surfaces in under a
  // second instead of ten (precedent: useDeviceMetrics.ts:270-274 -
  // `fetchMetrics(); intervalRef.current = setInterval(fetchMetrics, POLL_INTERVAL);`).
  const startSummaryPolling = () => {
    stopSummaryPolling();
    // THE SINGLE ARM POINT for the cross-row lock, and it sits ABOVE the immediate poll on purpose:
    // that poll can reach a terminal branch and call stopSummaryPolling itself, and the release
    // must be allowed to win. Both callers reach here only on a 202, so this is the moment a job is
    // known to exist.
    setRunningRecordingId(watchedRecordingIdRef.current);
    pollSummaryStatus();
    pollIntervalRef.current = setInterval(pollSummaryStatus, SUMMARY_POLL_INTERVAL_MS);
  };

  // Fire the POST and decide what to do with the answer. The poll timer starts ONLY on a 202 -
  // never optimistically on POST-fire, because against an old backend that would begin a
  // 404-restart loop while the first POST is still running.
  //
  // The second argument is the replacement intent, and it is read from NOWHERE else - not from a
  // ref, not from state, not from the agenda carried above. The 404 auto-restart below calls this
  // function with one argument, so the restart re-POSTs the agenda and can never ask for a
  // replacement. The accepted corner case: a recording that already has a stored report answers
  // the restart from cache, and the user clicks Generate Report again.
  const startSummaryJob = async (recordingId: string, regenerate: boolean = false) => {
    const generation = pollGenerationRef.current;
    try {
      // Read fresh on every call, including the restart's: the interval captured an older render,
      // but a ref is current by construction.
      const agenda = summaryAgendaRef.current;
      const response: SummaryPostResponse = regenerate
        ? await regenerateRecordingSummary(recordingId, agenda)
        : await generateRecordingSummary(recordingId, agenda);
      if (watchedRecordingIdRef.current !== recordingId || pollGenerationRef.current !== generation) {
        return;
      }

      // Cache hit: { success:true, recordingId, summary, cached:true }. Renders immediately -
      // byte-for-byte the path that already works in production.
      if (response.success && response.summary) {
        // The server just handed back a stored report, so this recording has one.
        noteReportPresence(recordingId, true);
        setSummaryContent(response.summary);
        setSummaryError("");
        resetReportProgress(recordingId);
        setSummaryLoading(false);
        return;
      }

      // 202: { status:'started' } or { status:'processing', stage }. Seed the stage from the
      // body so the modal is not stuck on "Starting..." until the first poll lands.
      if (response.status === "started" || response.status === "processing") {
        // A 202 from the NON-replacing entry point proves the recording had no stored report: that
        // path reads the cache first and answers 200 with the report whenever there is one
        // (canMeetController.ts, the `if (!regenerate)` cache branch). A 202 from the REPLACING
        // entry point proves nothing - it skipped the cache read - so it teaches this map nothing
        // and the recording keeps whatever it was.
        if (!regenerate) {
          noteReportPresence(recordingId, false);
        }
        applyReportProgress(response.status, String(response.stage ?? ""));
        startSummaryPolling();
        return;
      }

      // Neither a report nor a job acknowledgement. Do NOT start a timer against it.
      resetReportProgress(recordingId);
      setSummaryLoading(false);
      setSummaryError("AI Report is not available at this moment. Please contact support.");
    } catch (error: any) {
      if (watchedRecordingIdRef.current !== recordingId || pollGenerationRef.current !== generation) {
        return;
      }
      console.error("[MeetingHistory] Failed to start the summary job:", error);
      stopSummaryPolling();
      resetReportProgress(recordingId);
      setSummaryLoading(false);

      if (isSummaryAuthFailure(error)) {
        setSummaryError("Your session has expired. Please log in again and reopen the report.");
        return;
      }

      // The 20 s abort against a backend that is still doing the work synchronously (the
      // new-frontend/old-backend deploy window). The work completes and caches server-side, so
      // the honest instruction is to come back shortly - not "contact support".
      if (error?.code === "ECONNABORTED") {
        setSummaryError(
          "The report is still being generated. Please close this window and try again in a few minutes."
        );
        return;
      }

      setSummaryError(
        toSummaryErrorMessage(error, "AI Report is not available at this moment. Please contact support.")
      );
    }
  };

  // Come back to the job this client is ALREADY watching, without sending anything.
  //
  // ⚠ THIS FUNCTION MUST NOT TOUCH THE POLL. It arms nothing and releases nothing: the interval is
  // still running, watchedRecordingIdRef still names the job, pollGenerationRef still matches what
  // the live tick captured, and runningRecordingId is still set. Re-attaching is a dialog
  // operation only.
  //
  // Why it exists at all. summarizeRecording opens with stopSummaryPolling, which releases the
  // cross-row lock, and then sends the plain summary POST. On the row that owns a RUNNING job that
  // POST is answered from the cache - canMeetController.ts reads the stored summary before it
  // looks for a running job - so on a regenerate it returns the PRE-replace report, which the
  // cache-hit branch renders as if it were current and which never reaches startSummaryPolling.
  // Coming back to a running job would therefore drop the lock mid-job, re-enable every other row,
  // and show a stale report. Sending nothing avoids all three.
  //
  // The dialog then waits for the poll it re-joined. When that poll reports 'ready' it now finds
  // the dialog open, so it takes loadFinishedSummary and the FRESH report replaces whatever is on
  // screen.
  const reattachToRunningJob = (
    recordingId: string,
    meetingName: string,
    meetingDate: string
  ) => {
    setSummaryMeetingName(meetingName);
    setSummaryMeetingDate(meetingDate);
    setSummaryRecordingId(recordingId);
    setSummaryContent("");
    setSummaryError("");
    setSummaryLoading(true);
    setSummaryModalOpen(true);
    summaryModalOpenRef.current = true;

    // Display clock only, restored from the mirror the close handler left behind, and falling back
    // to the ceiling's origin when the mirror is gone. pollStartedAtRef itself is deliberately NOT
    // rewritten: this is the same job it has always been, and coming back must not buy it another
    // SUMMARY_POLL_CEILING_MS. pollRestartCountRef is left alone for the same reason - same job,
    // same restart budget.
    const restored = readReportTiming(recordingId);
    reportStartedAtRef.current = restored?.startedAt ?? pollStartedAtRef.current;
    stepEnteredAtRef.current = restored?.enteredAt ?? {};
    stepElapsedRef.current = restored?.elapsed ?? {};
    setReportStepElapsed(stepElapsedRef.current);
    setTranscriptActionError("");

    // One status read, so the dialog does not sit on "Starting..." for up to a full poll period.
    // A GET that starts nothing and bills nothing; the interval keeps its own schedule either way,
    // and pollSummaryStatus's own in-flight guard covers the case where a tick is already out.
    void pollSummaryStatus();
  };

  // The entry point for a user-initiated report. startSummaryJob cannot start one on its own:
  // this function is what sets watchedRecordingIdRef, bumps pollGenerationRef, resets the restart
  // count and the ceiling origin, opens the dialog and runs beginReportGeneration. A caller that
  // reached startSummaryJob directly would leave watchedRecordingIdRef at "", so the guard inside
  // it would fire AFTER the POST resolved - the request sent and billed, the answer discarded, no
  // modal, no polling, no report.
  const summarizeRecording = async (
    recordingId: string,
    meetingName: string,
    meetingDate: string,
    options: SummarizeOptions = {}
  ) => {
    // THE ONE ROW THIS FUNCTION DOES NOT SERVE: the one that owns a job already being watched.
    // Its AI Report button stays enabled by design - it is the way back to a report in flight -
    // and everything below this branch would retire that job's cycle. Re-attach instead, and send
    // nothing. See reattachToRunningJob for what the teardown-plus-POST path would have cost.
    //
    // The comparison is safe as a plain equality: runningRecordingId is "" when nothing is being
    // watched, and no recording id is ever "".
    if (runningRecordingId !== "" && recordingId === runningRecordingId) {
      reattachToRunningJob(recordingId, meetingName, meetingDate);
      return;
    }

    // Any poll left over from a previous recording must die before the ids change.
    stopSummaryPolling();

    setSummaryMeetingName(meetingName);
    setSummaryMeetingDate(meetingDate);
    setSummaryRecordingId(recordingId);
    setSummaryContent("");
    setSummaryError("");
    setSummaryLoading(true);
    setSummaryModalOpen(true);
    // Written beside the setState because the poll tick reads the ref, not the render.
    summaryModalOpenRef.current = true;

    // Refs, so every poll tick sees the CURRENT values rather than the render that armed the
    // interval. The race guard is the PAIR (watchedRecordingIdRef, pollGenerationRef): the id
    // alone stops A's late response painting into B's modal, but it does NOT stop a late
    // response from a PREVIOUS cycle of the SAME recording (close A, reopen A) — that response
    // still matches the id, and its `finally` would clear the NEW cycle's in-flight flag.
    // Bumping the generation here invalidates every response captured before this click. That
    // window is the whole ~10-minute job, not a single bounded request.
    watchedRecordingIdRef.current = recordingId;
    pollGenerationRef.current += 1;
    pollRestartCountRef.current = 0;
    pollStartedAtRef.current = Date.now();

    // Written BEFORE startSummaryJob so the first POST and every automatic re-POST of this job
    // read the same values. A call that passes no options clears both, which is what makes the
    // grid's plain AI Report click run default mode.
    summaryAgendaRef.current = {
      agendaText: options.agendaText ?? "",
      instructions: options.instructions ?? "",
    };
    setSummaryAgendaFileName(options.agendaFileName ?? "");

    // Display clock only. A restored record continues the counters; a fresh one starts from the
    // click. The ceiling above is deliberately NOT restored: seeding pollStartedAtRef with a
    // restored timestamp would trip SUMMARY_POLL_CEILING_MS on reopen.
    const restored = readReportTiming(recordingId);
    reportStartedAtRef.current = restored?.startedAt ?? pollStartedAtRef.current;
    stepEnteredAtRef.current = restored?.enteredAt ?? {};
    stepElapsedRef.current = restored?.elapsed ?? {};

    // The INDEX is not restored. beginReportGeneration below resets previousIndex to -1, releasing
    // rule R5's monotonic hold, and startSummaryPolling fires a poll immediately, so the true
    // index lands within a second - carrying the restored per-step durations with it.
    beginReportGeneration();
    setReportIndex(-1);
    setReportStepElapsed(stepElapsedRef.current);
    setReportTotalElapsed(0);
    setReportPhase("");
    setTranscriptActionError("");

    // `=== true` rather than a truthiness test, so nothing but an explicit intent can start a
    // replacement.
    await startSummaryJob(recordingId, options.regenerate === true);
  };

  // The agenda panel's lifecycle. Escape and an outside click both arrive as the Popover's
  // onClose. The panel's confirm dialog is a Modal of its own, rendered beside the Popover rather
  // than inside it, and MUI only lets the topmost open Modal answer Escape - so an open confirm
  // closes first and the panel second.
  const closeAgendaPopover = () => {
    setAgendaAnchorEl(null);
    setAgendaRow(null);
  };

  // Clicking the caret of the row whose panel is already open closes it. Any other caret opens
  // that row's panel; the remount clears the previous file, error and instructions.
  const handleAgendaCaretClick = (event: React.MouseEvent<HTMLElement>, row: AgendaRow) => {
    if (agendaRow?.recordingId === row.recordingId) {
      closeAgendaPopover();
      return;
    }
    setAgendaAnchorEl(event.currentTarget);
    setAgendaRow(row);
  };

  // The panel's confirmed-generate handler.
  //
  // THE ONE CALL SITE IN THIS FILE THAT ASKS FOR A REPLACEMENT. It runs only after the user has
  // clicked Generate Report in the panel AND confirmed the replace dialog. Scoping the
  // replacement intent to exactly this handler is deliberate: a second caller that passes it is
  // the money-safety failure the scoping exists to prevent.
  const handleAgendaGenerate = (payload: AgendaGeneratePayload) => {
    const row = agendaRow;
    if (!row) return;
    closeAgendaPopover();
    // One job at a time, enforced where it costs money and not only where it is drawn. The caret
    // that opens this panel is already disabled on every row while a job is in flight, so this
    // return is unreachable through the UI - which is the point. It is the guard that survives a
    // panel left open by some future change to how the caret is drawn, and it sits on the ONE path
    // in this file that asks for a replacement.
    if (runningRecordingId) return;
    void summarizeRecording(row.recordingId, row.meetingName, row.meetingDate, {
      agendaText: payload.agendaText,
      instructions: payload.instructions,
      regenerate: true,
      agendaFileName: payload.fileName,
    });
  };

  // Close summary modal. The background job keeps running in Node, and this client keeps asking
  // where it is - closing this window only takes the report off screen. Reopening later finds the
  // finished report in the cache instantly.
  const handleCloseSummaryModal = () => {
    // Is a job being watched right now? The interval's own existence is the answer, and it is the
    // exact condition: startSummaryPolling arms it only on a 202.
    const watchingJob = pollIntervalRef.current !== null;
    summaryModalOpenRef.current = false;

    // THE POLL DELIBERATELY OUTLIVES THIS DIALOG. It is the only thing that can release the
    // cross-row lock on a terminal result. A lock released by Close would be a hole: the user
    // closes the window, every row re-enables, and a second paid generation can start against
    // another recording while the first is still running.
    //
    // AND THE GENERATION IS NOT BUMPED while it survives. The live tick compares
    // pollGenerationRef against the value it captured; bumping here would make its own `finally`
    // skip, leaving pollInFlightRef stuck true - and every later tick would then return at the
    // in-flight check. The poll would be armed and permanently inert. A reopen bumps the
    // generation itself (summarizeRecording), which is what retires this cycle safely.
    if (!watchingJob) {
      stopSummaryPolling();
      watchedRecordingIdRef.current = "";
      // Retires this cycle. Any request still in flight now fails its generation check, so it
      // cannot paint into — or clear the in-flight flag of — the cycle a reopen starts next.
      pollGenerationRef.current += 1;
    }

    setSummaryModalOpen(false);
    setSummaryContent("");
    setSummaryError("");
    setSummaryRecordingId("");
    resetReportProgress();
    // MUST be here, and it is what keeps `summaryLoading` a dialog-lifetime flag rather than a
    // job-lifetime one. summarizeRecording carries no `finally { setSummaryLoading(false) }`, and
    // on the branch above that DOES clear the interval the poll's terminal branches never run.
    // Without this line the flag stays true for the rest of the page session: the modal reopens
    // pinned on the spinner branch, and the Download Transcript and Export PDF buttons stay
    // disabled until the page is reloaded. The job-lifetime flag is `runningRecordingId`, and it
    // is deliberately not touched here.
    setSummaryLoading(false);
  };

  // One download, two places a failure can land. The footer button may take over the modal body
  // because by then the report is already rendered and there is no step list to destroy. The row
  // action must not: mid-job, setSummaryError takes over the whole dialog body (the summaryError
  // branch below) and the user would lose the checklist they were watching.
  const downloadTranscriptReporting = async (report: (message: string) => void) => {
    if (!summaryRecordingId) return;

    setTranscriptDownloading(true);
    try {
      const response = await downloadTranscript(summaryRecordingId);
      if (response.accessLink) {
        window.open(response.accessLink, "_blank");
      } else {
        report("Failed to download transcript. Please try again.");
      }
    } catch (error: any) {
      console.error("[MeetingHistory] Failed to download transcript:", error);
      report("Failed to download transcript. Please try again later.");
    } finally {
      setTranscriptDownloading(false);
    }
  };

  const handleDownloadTranscript = () => {
    void downloadTranscriptReporting(setSummaryError);
  };

  // Row actions are dispatched by id, so a future step's action needs no new wiring here beyond
  // its own branch.
  //
  // One failure, two sinks, deliberately. Inline is the intended shape - a row action's failure
  // attaches to the panel, under the step list, instead of replacing the modal body - but it can
  // outlive the thing that renders it. The panel only exists while reportPhase is set, and
  // resetReportProgress clears reportPhase and transcriptActionError together, so a download still
  // in flight when the user closes the modal, or when the REPORT_READY_HOLD_MS timer fires after
  // the report lands, resolves into state nothing is showing. A 429 from the /meet rate limiter
  // shared with the poll is the failure this file already documents. Inline-only would leave that
  // user with a console.error and no other signal, which FRONTEND_ERROR_HANDLING_STANDARDS.MD
  // rules out three separate times ("Inform, Don't Ignore", "Console-only errors", and the
  // notifyError checklist item).
  //
  // The toast fires unconditionally rather than only when the panel is gone: this closure captures
  // a stale reportPhase, so a liveness test would need a ref written at every phase-set site,
  // which costs more than the redundancy it removes. Accepted price - while the panel is still up
  // the user sees the same failure twice, inline and as a toast. Do not delete either as a
  // duplicate.
  const handleProgressAction = (actionId: string) => {
    if (actionId !== "transcript") {
      console.warn("[MeetingHistory] Unhandled report step action:", actionId);
      return;
    }
    setTranscriptActionError("");
    void downloadTranscriptReporting((message) => {
      setTranscriptActionError(message);
      notifyError(message);
    });
  };

  // Re-runs the same entry point the grid button does. summarizeRecording bumps the poll
  // generation and resets previousIndex to -1, so rule R5's monotonic hold does not carry the
  // failed job's index into the new one. A re-POST after a failed job starts a fresh job.
  //
  // This is a SECOND user click, not an automatic path - the panel draws it on a failed job - and
  // it carries the failed job's agenda and instructions back in so the retry runs in the same
  // mode. It never carries the replacement intent, which is deliberately scoped to the panel's
  // confirmed-generate handler alone. A retry after a failed replacement therefore re-runs in
  // agenda mode, and where a report is already stored the backend answers from cache and the user
  // clicks Generate Report again.
  const handleRetryReport = () => {
    if (!summaryRecordingId) return;
    const agenda = summaryAgendaRef.current;
    void summarizeRecording(summaryRecordingId, summaryMeetingName, summaryMeetingDate, {
      agendaText: agenda.agendaText,
      instructions: agenda.instructions,
      agendaFileName: summaryAgendaFileName,
    });
  };

  // Export AI report as PDF
  const handleExportPdf = async () => {
    if (!summaryRecordingId || !summaryContent) return;

    setPdfExporting(true);
    try {
      const response = await exportReportPdf(summaryRecordingId, summaryMeetingName, summaryMeetingDate);
      if (response.success && response.fileId) {
        window.open(`/api3.2/bucket/${response.fileId}`, '_blank');
      } else {
        setSummaryError("Failed to export PDF. Please try again.");
      }
    } catch (error: any) {
      console.error("[MeetingHistory] Failed to export PDF:", error);
      setSummaryError("Failed to export PDF. Please try again later.");
    } finally {
      setPdfExporting(false);
    }
  };

  // Calculate meeting duration in HH:mm:ss format
  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const durationMs = end - start;

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Fetch all past meeting recordings and prepare them for display
  const getMeetingHistory = async () => {
    try {
      const response = await fetchMeeting();
      if (response?.results) {
        const formattedRows = response.results.map((recording: any) => {
          // Clean raw room name: remove leading "/", fallback to default
          const rawRoomName = recording.roomName?.trim() || "Unnamed Meeting";
          const originalRoomName = rawRoomName.startsWith("/")
            ? rawRoomName.substring(1)
            : rawRoomName;

          // Remove "-canmeet" tag from room name
          const lowerCaseRoomName = originalRoomName.toLowerCase();
          const separator = "-canmeet";
          const index = lowerCaseRoomName.indexOf(separator);
          const rawDisplayName =
            index !== -1
              ? originalRoomName.substring(0, index)
              : originalRoomName;

          // Replace hyphens with spaces
          const nameWithSpaces = rawDisplayName.replace(/-/g, " ");

          // Capitalize only auto-generated names (starting with "meeting-on-")
          const displayedRoomName = rawDisplayName.startsWith("meeting-on-")
            ? formatAutoGeneratedName(nameWithSpaces)
            : nameWithSpaces;

          // Return a formatted row for the DataGrid
          return {
            id: recording.recordingId,
            roomName: originalRoomName, // Full original room name (with tags)
            displayedRoomName,          // Cleaned name for table display
            startDate: new Date(recording.startDate).toLocaleString(),
            duration: calculateDuration(recording.startDate, recording.endDate),
            size: recording.sizeInMegaBytes,
            filename: recording.filename,
            recordingId: recording.recordingId,
          };
        });

        setRows(formattedRows); // Update table data
      }
    } catch (error) {
      console.error("[MeetingHistory] Failed to fetch meetings:", error);
      notifyError("Failed to load meeting history");
    }
  };

  // Define DataGrid columns
  const columns: GridColDef[] = [
    { field: "displayedRoomName", headerName: "Meeting Name", width: 300 }, //'Room Name' changed to 'Meeting Name' for display
    { field: "startDate", headerName: "Start Date", width: 200 },
    // No column sets `flex`, so the DataGrid never stretches these widths and the five declared
    // widths must sum below the grid's client width at a 1280 px viewport - past that the grid
    // scrolls horizontally and the Delete icon in the Actions column clips until the user scrolls
    // sideways. Duration is the column with slack: its values are a fixed `HH:mm:ss`
    // (calculateDuration), so the width here covers eight characters plus the DataGrid's padding
    // with room to spare. The Actions column is deliberately untouched - it spends nearly all of
    // its width already, and shaving it risks wrapping the three-control cluster it holds.
    { field: "duration", headerName: "Duration", width: 120 },
    { field: "size", headerName: "Size of Recording (MB)", width: 175 },
    {
      field: "actions",
      headerName: "Actions",
      width: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        // Two expressions, not one, because the two halves of the split control answer different
        // questions while a job runs in the background.
        //
        // `summaryLoading` is the dialog's flag: it covers the window between the click and the
        // first answer, and the whole time the dialog is on screen. `runningRecordingId` is the
        // job's flag: it covers the rest of the job's life, including after the dialog is closed,
        // which is the state AI_REPORT_RUNNING_TOOLTIP is seen in.
        const jobRunning = runningRecordingId !== "";
        const jobRunningElsewhere = jobRunning && runningRecordingId !== params.row.recordingId;
        // The main button is the way BACK to a running report - the progress dialog's own hint
        // tells the user to close it and come back - so the row that owns the job keeps it.
        const reportDisabled = summaryLoading || jobRunningElsewhere;
        // The caret starts a SECOND generation, so it closes on every row while any job runs,
        // including the row that owns the job.
        const agendaDisabled = summaryLoading || jobRunning;
        return (
        <Stack direction="row" spacing={1} alignItems="center">
          {/* One split control, not two buttons: the caret is an option on the AI Report action,
              not a fourth peer action in this column. The wrapping Box is what carries the single
              shadow and keeps the two halves flush - the Stack's spacing would otherwise put 8 px
              between them. */}
          <Box sx={{ display: "inline-flex", borderRadius: 1, boxShadow: 2 }}>
            {/* A disabled MUI Button emits no pointer events, so each half is wrapped in a span
                inside its own Tooltip. STooltip is no help here - it hardcodes an IconButton and
                takes an `icon` prop, so it cannot wrap a Button. An empty title renders no
                tooltip, which is how the enabled main button stays silent. */}
            <Tooltip title={reportDisabled ? AI_REPORT_RUNNING_TOOLTIP : ""}>
              <span style={{ display: "inline-flex" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => summarizeRecording(params.row.recordingId, params.row.displayedRoomName, params.row.startDate)}
                  // One report at a time, across ALL OTHER rows: a second POST while one is running
                  // would race the same poll cycle and briefly put two /summary/status requests in
                  // flight. The backend tolerates it; the UI just should not invite it. The row that
                  // owns the running job is the exception - clicking it is how the user comes back.
                  disabled={reportDisabled}
                  sx={{
                    boxShadow: "none",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                >
                  AI Report
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={agendaDisabled ? AI_REPORT_RUNNING_TOOLTIP : AGENDA_CARET_TOOLTIP}>
              <span style={{ display: "inline-flex" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  aria-label={AGENDA_CARET_TOOLTIP}
                  onClick={(event) =>
                    handleAgendaCaretClick(event, {
                      recordingId: params.row.recordingId,
                      meetingName: params.row.displayedRoomName,
                      meetingDate: params.row.startDate,
                    })
                  }
                  // STRICTER than the main button, deliberately. The main button has to reopen
                  // the running row, but this half opens the panel that starts a new generation,
                  // and one is already being paid for.
                  disabled={agendaDisabled}
                  sx={{
                    boxShadow: "none",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: "1px solid",
                    borderColor: "secondary.dark",
                    minWidth: 24,
                    px: 0.5,
                    bgcolor:
                      agendaRow?.recordingId === params.row.recordingId
                        ? "secondary.dark"
                        : undefined,
                  }}
                >
                  <KeyboardArrowDownIcon
                    fontSize="small"
                    sx={{
                      transition: "transform 0.2s",
                      transform:
                        agendaRow?.recordingId === params.row.recordingId
                          ? "rotate(180deg)"
                          : "none",
                    }}
                  />
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Tooltip title="Download">
            <IconButton
              color="primary"
              size="small"
              onClick={() => downloadRecording(params.row.recordingId)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => deleteRecording(params.row.recordingId)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        );
      },
    },
  ];

  // The failed step's own sentence when it has one, else the server's words, else the generic.
  // The step wins on purpose: the server always sends SOMETHING, so a server-first order leaves
  // every failureFallback in reportSteps.ts unreachable and puts a raw code in front of the user -
  // an observed example is "AI_REPORT_UNAVAILABLE: OpenAI API error: 401" in this alert.
  // Truthiness, not ??, because summaryError is "" when unset.
  const reportPanelMessage = (() => {
    if (reportPhase === "failed") {
      const step = reportIndex >= 0 ? REPORT_STEPS[reportIndex] : undefined;
      return (
        step?.failureFallback ||
        summaryError ||
        "AI Report is not available at this moment. Please contact support."
      );
    }
    if (reportPhase === "detached") return summaryError;
    return undefined;
  })();

  // Render the UI
  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        p: 3,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        Past Meeting Recordings
      </Typography>
      <Paper>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={20}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'No previous recordings.',
            }}
          />
        </Box>
      </Paper>

      {/* The agenda attach panel, anchored to the caret that opened it. Keyed on the recording
          id: a different row's caret mounts a fresh panel, so the file, the error and the
          instructions from the previous row cannot survive into it. */}
      <AgendaAttachPopover
        key={agendaRow?.recordingId ?? "no-agenda-row"}
        open={Boolean(agendaAnchorEl && agendaRow)}
        anchorEl={agendaAnchorEl}
        meetingName={agendaRow?.meetingName ?? ""}
        // Which confirm copy the panel shows. `!== false` is the whole decision: only a recording
        // this session has PROVEN to have no stored report reads as fresh, and everything else -
        // including every row on a page that has just loaded - keeps the replace warning.
        hasExistingReport={agendaRow ? reportPresence[agendaRow.recordingId] !== false : true}
        onClose={closeAgendaPopover}
        onGenerate={handleAgendaGenerate}
      />

      {/* Summary Modal */}
      <Dialog
        open={summaryModalOpen}
        onClose={handleCloseSummaryModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                {summaryMeetingName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summaryMeetingDate}
              </Typography>
              {/* Names the agenda that rode along, when one did. It sits in the TITLE and not in
                  the content below, whose four-branch render order is load-bearing. */}
              {summaryAgendaFileName && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    icon={<AttachFileIcon />}
                    label={summaryAgendaFileName}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}
            </Box>
            <IconButton onClick={handleCloseSummaryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* The order of these tests is load-bearing, not stylistic. summaryLoading stays
              true for a job's whole life (the 202 that starts it never clears it) and
              summaryError is set on both terminal phases, failed and detached - so reportPhase
              has to be asked first or the panel never renders. Hoisting summaryLoading hides
              the checklist for the whole job; hoisting summaryError - the reflexive "errors
              first" tidy-up - trades a failed job's checklist, which still offers Download
              transcript on a transcribing row that finished, for a bare red string. Either
              silently reverts that behaviour, and no test in this repo would catch it. */}
          {reportPhase ? (
            <ReportProgressPanel
              steps={REPORT_STEPS}
              activeIndex={reportIndex}
              phase={reportPhase}
              stepElapsedMs={reportStepElapsed}
              totalElapsedMs={reportTotalElapsed}
              message={reportPanelMessage}
              isAdmin={isAdmin}
              actionBusyId={transcriptDownloading ? "transcript" : null}
              actionError={transcriptActionError || null}
              onAction={handleProgressAction}
              onRetry={handleRetryReport}
            />
          ) : summaryLoading ? (
            // Between the click and the first answer there is no phase yet. One spinner, one
            // line - the panel would be three grey rows saying nothing.
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }} color="text.secondary">
                Starting...
              </Typography>
            </Box>
          ) : summaryError ? (
            <Typography color="error">{summaryError}</Typography>
          ) : (
            <Box sx={{ position: "relative" }}>
              {/* Summary content - shown fully for admins, partially for others */}
              <Box
                sx={{
                  maxHeight: isAdmin ? "none" : "300px",
                  overflow: "hidden",
                }}
              >
                {(() => {
                  const structured = parseSummary(summaryContent);
                  if (structured) {
                    return <StructuredSummaryView summary={structured} />;
                  }
                  return (
                    <Box sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                      {summaryContent}
                    </Box>
                  );
                })()}
              </Box>

              {/* Subscription overlay for non-admin users */}
              {!isAdmin && summaryContent && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "220px",
                    background:
                      "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 30%, rgba(255,255,255,1) 60%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    pb: 2,
                  }}
                >
                  <LockIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold" textAlign="center">
                    Subscribe to unlock the full report
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mt: 0.5 }}
                  >
                    Get full AI meeting notes, transcripts, and PDF exports.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mt: 0.5 }}
                  > 
                   Email support@streamdata.com
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Chip
            icon={<AutoAwesomeIcon />}
            label="Powered by CANai"
            variant="outlined"
            size="small"
            sx={{
              borderColor: "secondary.main",
              color: "secondary.main",
              "& .MuiChip-icon": { color: "secondary.main" },
            }}
          />
          <Stack direction="row" spacing={1}>
            {isAdmin && summaryContent && !summaryError && (
              <Button
                onClick={handleDownloadTranscript}
                variant="outlined"
                startIcon={transcriptDownloading ? <CircularProgress size={16} /> : <DescriptionIcon />}
                disabled={transcriptDownloading || summaryLoading}
              >
                {transcriptDownloading ? "Downloading..." : "Download Transcript"}
              </Button>
            )}
            {isAdmin && summaryContent && !summaryError && (
              <Button
                onClick={handleExportPdf}
                variant="outlined"
                startIcon={pdfExporting ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                disabled={pdfExporting || summaryLoading}
              >
                {pdfExporting ? "Exporting..." : "Export PDF"}
              </Button>
            )}
            <Button onClick={handleCloseSummaryModal} variant="contained">
              Close
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RecordingHistory;
